import { useCurrentUserQuery } from "@/query/currentUserQuery"
import { useShopDataQuery } from "@/query/shopDataQuery"
import { BeautyLayout, NavigationItem } from "@okeys/shared-react-components"
import { DataShopTable } from "@okeys/shared-utils"
import { signOut } from "aws-amplify/auth"
import {
  CalendarDays,
  CalendarX2,
  ClipboardList,
  CreditCard,
  FolderClock,
  Gift,
  LayoutDashboard,
  Store,
  User,
} from "lucide-react"
import { useEffect, useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"

export default function MainLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [isDarkMode, setIsDarkMode] = useState(false)

  // Initialiser le thème au chargement
  useEffect(() => {
    // Appliquer la classe light par défaut au body
    document.body.classList.add("light")
  }, [])

  const { shopData, pendingShopData, errorShopData } = useShopDataQuery()
  const typedShopData = shopData as DataShopTable
  const { currentUser, isLoading, isPending, isError } = useCurrentUserQuery()

  const navigationItems: NavigationItem[] = [
    {
      label: "Dashboard",
      href: "/",
      icon: LayoutDashboard,
    },
    {
      label: "Caisse",
      href: "/cash-register",
      icon: CreditCard,
    },
    {
      label: "Rendez-vous",
      href: "/bookings",
      icon: CalendarDays,
    },
    {
      label: "Agenda",
      href: "/schedules",
      icon: CalendarDays,
    },
    {
      label: "Indisponibilités",
      href: "/unavailability",
      icon: CalendarX2,
    },
    {
      label: "Clients",
      href: "/clients",
      icon: User,
    },
    {
      label: "Paramètres boutique",
      href: "/shop-settings",
      icon: Store,
    },
    {
      label: "Collaborateurs",
      href: "/collaborateurs",
      icon: User,
    },
    {
      label: "Liste des services",
      href: "/listServices",
      icon: ClipboardList,
    },
    {
      label: "Promotions",
      href: "/promotions",
      icon: Gift,
    },
    {
      label: "Historique",
      href: "/historic",
      icon: FolderClock,
    },
  ]

  // Modifier les items pour utiliser navigate au lieu de href
  const navigationItemsWithClick = navigationItems.map(item => ({
    ...item,
    onClick: () => navigate(item.href)
  }))

  // Déterminer l'élément actif basé sur l'URL actuelle
  const getActiveMenuItem = () => {
    const currentPath = location.pathname
    const item = navigationItems.find(item => item.href === currentPath)
    return item?.label || "Dashboard"
  }

  const handleThemeToggle = () => {
    const newTheme = !isDarkMode
    setIsDarkMode(newTheme)

    // Appliquer le thème au body
    if (newTheme) {
      document.body.classList.add("dark")
      document.body.classList.remove("light")
    } else {
      document.body.classList.add("light")
      document.body.classList.remove("dark")
    }
  }

  if (isLoading || isPending || pendingShopData) {
    return <div>Chargement...</div>
  }

  if (isError || errorShopData || !shopData) {
    return <div>Erreur lors de la récupération des données</div>
  }

  const pageConfig = getPageConfig(location.pathname)

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error)
    }
  }

  return (
    <BeautyLayout
      title={pageConfig.title}
      subtitle={pageConfig.subtitle}
      icon={pageConfig.icon}
      userName={currentUser?.signInDetails?.loginId || "Utilisateur"}
      userAvatar={undefined}
      shopLogo={typedShopData.urlLogo}
      shopName={typedShopData.shopName}
      onThemeToggle={handleThemeToggle}
      onSignOut={handleSignOut}
      isDarkMode={isDarkMode}
      activeMenuItem={getActiveMenuItem()}
      navigationItems={navigationItemsWithClick}
    >
      <Outlet />
    </BeautyLayout>
  )
}

// Fonction helper pour obtenir la configuration de la page
function getPageConfig(pathname: string): { title: string; subtitle: string; icon: any } {
  const configs: Record<string, { title: string; subtitle: string; icon: any }> = {
    "/": {
      title: "Dashboard",
      subtitle: "Statistiques de votre boutique",
      icon: LayoutDashboard,
    },
    "/cash-register": {
      title: "Caisse",
      subtitle: "Gestion des encaissements",
      icon: CreditCard,
    },
    "/bookings": {
      title: "Rendez-vous",
      subtitle: "Gestion des réservations",
      icon: CalendarDays,
    },
    "/schedules": {
      title: "Agenda",
      subtitle: "Planning et disponibilités",
      icon: CalendarDays,
    },
    "/unavailability": {
      title: "Indisponibilités",
      subtitle: "Gestion des absences",
      icon: CalendarX2,
    },
    "/clients": {
      title: "Clients",
      subtitle: "Gestion de la clientèle",
      icon: User,
    },
    "/shop-settings": {
      title: "Paramètres boutique",
      subtitle: "Configuration de l'établissement",
      icon: Store,
    },
    "/collaborateurs": {
      title: "Collaborateurs",
      subtitle: "Gestion de l'équipe",
      icon: User,
    },
    "/listServices": {
      title: "Liste des services",
      subtitle: "Catalogue des prestations",
      icon: ClipboardList,
    },
    "/promotions": {
      title: "Promotions",
      subtitle: "Offres et réductions",
      icon: Gift,
    },
    "/historic": {
      title: "Historique",
      subtitle: "Historique des opérations",
      icon: FolderClock,
    },
  }
  return configs[pathname] || {
    title: "Dashboard",
    subtitle: "Statistiques de votre boutique",
    icon: LayoutDashboard,
  }
}
